import type { FC, ReactNode } from "react";
import { useEffect, useState } from "react";
import { Alert, Button, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { AnswerValidation, OvertimeModal, Tip } from "components";
import AnswerVideo from "components/Video/AnswerVideo";
import { SolutionModal } from "features";

import type { AssignmentPracticeFragment } from "api/generated";

import { useAppDispatch, useAppSelector } from "store/hooks";
import { getTip, setTip } from "store/slices/exercise";
import {
  getLearningTime,
  measureLearningTime,
  resetLearningTimer,
  startLearningTimer /*, stopLearningTimer */,
} from "store/slices/timer";
import { setIsSoundMuted } from "store/slices/user";

import { eventEmitter, Events } from "utils/events/EventEmitter";
import { isMobile } from "utils/isMobile";

import IconCheck from "images/icons/check.svg";
import IconCross from "images/icons/close.svg";
import { gtmTaskEvent } from "../../utils/gtmEvents";

interface Props {
  addIcon?: boolean;
  assignment: AssignmentPracticeFragment | undefined | null;
  children: ReactNode;
  index: number;
  answerValidation?: {
    userAnswer: string;
    correctAnswer: string;
    scoreMax: number;
  };
  subject?: string | null;
  timeToLearn?: number;
  tipDisabled?: boolean;
  type: "success" | "warning" | "danger" | undefined;
  userAssignmentId: string;
  hideVideo?: boolean;
  isSelfEvaluated?: boolean;
  onNext?: () => void;
  toggleModals?: (index: number | null) => void;
}

const AnswerExercise: FC<Props> = ({
  answerValidation,
  assignment,
  children,
  index,
  subject,
  timeToLearn,
  type,
  userAssignmentId,
  addIcon = false,
  tipDisabled = false,
  hideVideo = false,
  isSelfEvaluated = false,
  toggleModals,
  onNext,
}) => {
  if (assignment === undefined || assignment === null) return null;

  const dispatch = useAppDispatch();
  const time = useAppSelector(getLearningTime);
  const tip = useAppSelector(getTip(userAssignmentId, index));
  const { t } = useTranslation(["exercise"]);
  const location = useLocation();

  const isOnboarding = location.pathname.includes("onboarding");

  const [isModalVisible, setModalVisibility] = useState(false);
  const [isOvertimeModalVisible, setOvertimeModalVisibility] = useState(false);

  useEffect(() => {
    eventEmitter.dispatch(Events.ONBOARDING_UPDATE);
  }, []);

  useEffect(() => {
    if (isModalVisible || timeToLearn === undefined || onNext === undefined) return;

    dispatch(resetLearningTimer());
    dispatch(startLearningTimer());

    const interval = setInterval(() => {
      dispatch(measureLearningTime());
    }, 10000);

    return () => {
      clearInterval(interval);
      dispatch(resetLearningTimer());
    };
  }, [isModalVisible]);

  useEffect(() => {
    if (timeToLearn !== undefined && time >= timeToLearn) {
      setModalVisibility(false);
      setOvertimeModalVisibility(true);
    }
  }, [time]);

  const getIcon = () =>
    type === "success" ? (
      <IconCheck className={"text-success ms-4px"} />
    ) : (
      <IconCross className={"text-danger ms-4px"} />
    );

  const toggleModal = () => {
    if (toggleModals !== undefined) {
      toggleModals(isModalVisible ? null : index);
    }
    setModalVisibility(visibility => {
      dispatch(setIsSoundMuted(!visibility));
      return !visibility;
    });
  };

  const handleTipClick = (index: number, tip: boolean) => {
    dispatch(setTip({ userAssignmentId, index, tip }));
  };

  useEffect(() => {
    if (isModalVisible) {
      gtmTaskEvent("task-analysis", {
        id: assignment.id,
        subject,
      });
    }
  }, [isModalVisible]);

  const analysis =
    assignment.analysis?.hasSharedAnalysis === true
      ? assignment.analysis.sharedAnalysis
      : assignment.assignment?.questions[index].solutionAnalysis;

  return (
    <>
      <Alert variant={type ?? "danger"} className="flex-grow-1">
        <Row className="flex-nowrap">
          <Col xs={"auto"} className={"d-flex align-items-center my-auto flex-grow-1 flex-shrink-1"}>
            {children}
            {addIcon && getIcon()}
          </Col>
          <Col xs={"auto"} className={"ms-auto my-auto d-flex align-items-center"}>
            {!isSelfEvaluated && (
              <Tip type={type} isChecked={tip} index={index} onClick={handleTipClick} isDisabled={tipDisabled} />
            )}
            {answerValidation !== undefined ? (
              <AnswerValidation
                assignmentId={assignment.id}
                data={answerValidation}
                index={index}
                onClick={handleTipClick}
                type={type}
                userAssignmentId={userAssignmentId}
              />
            ) : null}
            <Button variant={"select"} className={"btn-shadow ms-4px btn-smaller"} onClick={toggleModal} id="analysis">
              {isMobile() ? t("btn.analysisMobile") : t("btn.analysisDesktop")}
            </Button>
          </Col>
        </Row>
      </Alert>

      {isOnboarding || hideVideo ? null : (
        <AnswerVideo videoId={analysis?.explainerVideo} answerType={type ?? "danger"} />
      )}

      <SolutionModal
        isShown={isModalVisible}
        assignment={assignment}
        questionIndex={index}
        answerType={type ?? "danger"}
        toggleModal={toggleModal}
        userAssignmentId={userAssignmentId}
      >
        {children}
      </SolutionModal>
      {isOvertimeModalVisible && onNext !== undefined ? (
        <OvertimeModal userAssignmentId={userAssignmentId} onClose={onNext} />
      ) : null}
    </>
  );
};

export default AnswerExercise;
